import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { FiUsers, FiCalendar, FiBarChart2, FiAlertCircle } from 'react-icons/fi';
import ErrorBoundary from '../components/ErrorBoundary';
import SBFPaymentAnalytics from '../components/SBFPaymentAnalytics';
import SBFMemberAnalytics from '../components/SBFMemberAnalytics';

const SBFDashboardContent = () => {
    const [stats, setStats] = useState({
        totalMembers: 0,
        activeMembers: 0,
        cancelledMembers: 0,
        upcomingRenewals: [],
        thisMonth: {
            month: '',
            totalPaidMember: 0,
            totalSGDAmount: 0
        }
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/sbfmembers/dashboard-stats`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );

                if (response.data.success) {
                    setStats(response.data.data);
                }
            } catch (err) {
                setError(`Failed to fetch statistics: ${err.response?.data?.error || err.message}`);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-SG');
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-SG', {
            style: 'currency',
            currency: 'SGD'
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 w-full max-w-none px-8">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-6">SBF Dashboard Overview</h2>

                {error && (
                    <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-md flex items-center">
                        <FiAlertCircle className="mr-2" />
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Total Members Card */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiUsers className="h-8 w-8 text-blue-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-blue-800">Total Members</h3>
                                <p className="text-3xl font-bold text-blue-600">{stats.totalMembers}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-blue-700">Active</span>
                                <span className="font-medium text-blue-800">{stats.activeMembers}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-red-700">Cancelled</span>
                                <span className="font-medium text-red-800">{stats.cancelledMembers}</span>
                            </div>
                        </div>
                    </div>

                    {/* Upcoming 3 Renewals Card */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiCalendar className="h-8 w-8 text-purple-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-purple-800">Upcoming 3 Renewals</h3>
                                
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            {stats.upcomingRenewals && stats.upcomingRenewals.length > 0 ? (
                                stats.upcomingRenewals.map((renewal, index) => (
                                    <div key={index} className="flex flex-col text-sm border-b border-purple-200 pb-2">
                                        <div className="font-medium text-purple-900">
                                            {renewal.sbfAdsID}
                                            {renewal.sbfQuote && ` - ${renewal.sbfQuote}`}
                                            {renewal.sbfAdsName && ` - ${renewal.sbfAdsName}`}
                                        </div>
                                        <div className="text-purple-600 text-xs">
                                            Renewal: {formatDate(renewal.renewalDate)}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-sm text-purple-700">
                                    No upcoming renewals 
                                </div>
                            )}
                        </div>
                    </div>

                    {/* This Month Analytics Card */}
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="flex items-center">
                            <FiBarChart2 className="h-8 w-8 text-yellow-600" />
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-yellow-800">This Month Analytics</h3>
                                <p className="text-sm font-bold text-yellow-600">{stats.thisMonth.month}</p>
                            </div>
                        </div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-yellow-700">Total Paid Member</span>
                                <span className="font-medium text-yellow-800">{stats.thisMonth.totalPaidMember}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-yellow-700">Total SGD Amount</span>
                                <span className="font-medium text-yellow-800">
                                    {formatAmount(stats.thisMonth.totalSGDAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SBF Payment Analytics Section */}
            <SBFPaymentAnalytics />

            {/* SBF Member Analytics Section */}
            <div className="pb-8">
                <SBFMemberAnalytics />
            </div>
        </div>
    );
};

const SBFDashboard = () => {
    return (
        <Layout>
            <ErrorBoundary
                fallbackTitle="SBF Dashboard Error"
                fallbackDescription="An error occurred while loading the SBF dashboard. Please try again or contact support if the problem persists."
            >
                <SBFDashboardContent />
            </ErrorBoundary>
        </Layout>
    );
};

export default SBFDashboard;
